import React, {
    ReactElement,
    useEffect,
    useState,
} from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { BsTrash } from "react-icons/bs";
import classNames from "classnames";
import {
    ActionContextType,
    FormField,
    FormType,
    WorkspaceEvent,
    useGetRequestTypesQuery,
    useGetWorkspaceEventsQuery,
    useSaveWorkspaceEventsMutation,
} from "../../../../__generated__/api";
import { Filters } from "../../../../components/filters/Filters";
import { LoadingIcon } from "../../../../components/loading/Loading";
import { NOBODY_GROUP_ID } from "../../../../components/pickers/user-groups/UserGroups";
import useArticleContext from "../../../../contexts/HelpContext";
import usePortal from "../../../../contexts/PortalContext";
import useRouting, { Link, useParams } from "../../../../contexts/Routing";
import useCurrentSession from "../../../../contexts/session";
import { ErrorRemote } from "../../../error/Error";
import ActionFormSelected from "./ActionForm";
import WorkflowPicker from "../../../../components/workflow/WorkflowPicker";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import { cloneWithoutTypename } from "../../../../api";
import useDeleteConfirm from "../../../../components/confirm/Delete";
import { useTheme } from "../../../../contexts/Theme";
import ActionsV3 from "./ActionsV3";
import ActionFormV3 from "./ActionFormV3";

export function ActionFormList({
    type, title, singular, setting, description,
}: ActionFormListProps): ReactElement {
    const { data, error, loading: isLoading } = useGetRequestTypesQuery({
        fetchPolicy: "no-cache",
        variables: {
            type,
        },
    });

    const { Paths } = useRouting();
    const userSession = useCurrentSession();

    const [formFilters, setFormFilters] = useState<Set<string>>(() => new Set(userSession.user?.userGroups.toIdList("id")));
    const [formSearch, setFormSearch] = useState<string>("");

    if (isLoading) {
        return <LoadingIcon className="col-6" />;
    }
    if (error || !data) {
        return <ErrorRemote error={error} />;
    }

    let { forms } = data;

    if (formFilters.size > 0 && data.userGroups.isNotEmpty) {
        forms = forms?.filter(row => row.userGroupIds?.isEmpty
            || row.userGroupIds?.firstElement === NOBODY_GROUP_ID
            || row.userGroupIds?.some(id => formFilters.has(id)));
    }
    if (formSearch) {
        const search = formSearch.toLocaleLowerCase();
        forms = forms?.filter(row => row.name.toLocaleLowerCase().includes(search)
            || row.description?.toLocaleLowerCase().includes(search));
    }

    return (

        <div className="border p-3 rounded bg-white col-6">
            {data.forms?.isNotEmpty ? (
                <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="text-light mb-0">
                            {title}
                        </h5>
                        <Link
                            to={Paths.ADMINISTRATOR_SETTING_ID}
                            params={{
                                id: "new",
                                setting,
                            }}
                            className="btn btn-none btn-sm"
                        >
                            <AiOutlinePlus />
                            {" "}
                            {singular}
                        </Link>
                    </div>
                    <Filters
                        categories={data.userGroups.map(row => ({
                            label: row.name,
                            value: row.id,
                        }))}
                        selectedCategories={formFilters}
                        onSelected={setFormFilters}
                        search={formSearch}
                        onSetSearch={setFormSearch}
                    />
                    {forms.isNotEmpty ? (

                        <div className="list-group">
                            {forms?.map(field => (
                                <Link
                                    to={Paths.ADMINISTRATOR_SETTING_ID}
                                    key={field.id}
                                    params={{
                                        id: field.id,
                                        setting,
                                    }}
                                    className="list-group-item list-group-item-action flex-column align-items-start"
                                >
                                    <div className="d-flex w-100 align-items-end mb-2">
                                        <h5 className="mb-0">
                                            {field.name}
                                            {" "}
                                        </h5>

                                        <small className="ms-3 text-light " />
                                    </div>
                                    <small>{field.description}</small>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center text-light">
                            No
                            {" "}
                            {title.toLowerCase()}
                            {" "}
                            found matching your filters.
                        </div>
                    )}
                    <div className="d-flex justify-content-end align-items-center mt-3">

                        <Link
                            to={Paths.ADMINISTRATOR_SETTING_ID}
                            params={{
                                id: "new",
                                setting,
                            }}
                            className="btn btn-secondary btn-sm"
                        >
                            <AiOutlinePlus />
                            {" "}
                            {singular}
                        </Link>
                    </div>

                </>
            ) : (
                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-light my-4">
                    <h5 className="text-light">
                        {title}
                    </h5>
                    <Link
                        to={Paths.ADMINISTRATOR_SETTING_ID}
                        params={{
                            id: "new",
                            setting,
                        }}
                        className="btn btn-secondary btn-sm my-3"
                    >
                        <AiOutlinePlus />
                        {" "}
                        Add
                        {" "}
                        {singular}
                    </Link>
                    <small>
                        {description}
                    </small>
                </div>

            )}

        </div>

    );
}

interface ActionFormListProps {
    type: FormType;
    title: string;
    singular: string;
    setting: string;
    description: string;
}

export function ActionPage() {
    useArticleContext("urn:fq:article:7c8f62a03cbb41bc8a73612c329879f4");

    const { data, loading } = useGetWorkspaceEventsQuery();
    const [saveWorkspaceEvents] = useSaveWorkspaceEventsMutation();
    const [events, setEvents] = useState<Partial<WorkspaceEvent>[]>(data?.workspaceEvents ?? []);
    useEffect(() => {
        setEvents(data?.workspaceEvents ?? []);
    }, [data?.workspaceEventTypes]);

    const eventTypes = data?.workspaceEventTypes?.toIdMap("id");
    const handledEvents = new Set(events.toIdList("eventTypeId"));
    const deleted = useDeleteConfirm<number>(index => {
        const copy = [...events];
        copy.splice(index, 1);
        setEvents(copy);
    }, index => eventTypes?.[events[index].eventTypeId].label);

    const saveEvents = (index: number, workflowId: string) => {
        LoggingHandler.instance.wrap(async () => {
            const copy = [...events];
            copy[index].workflowId = workflowId;
            setEvents(copy);
            await saveWorkspaceEvents({
                variables: {
                    events: cloneWithoutTypename(copy) as WorkspaceEvent[],
                },
            });
        });
    };

    return (
        <div>

            {data?.workspaceEventTypes?.isNotEmpty && (
                <div className="border p-3 mb-4 rounded bg-white">
                    <h5 className="text-light mb-0">
                        Events
                    </h5>
                    {deleted.element}
                    {loading ? <LoadingIcon className="col-6" /> : (

                        <div>

                            <p className="text-light">
                                Events allow you to execute custom actions when certain events occur in your workspace such as
                                when Foqal is added to a channel, when a user is added, or when a login occurs into Foqal.
                            </p>

                            <div className="list-group">
                                {events.map((event, index) => {
                                    const eventType = eventTypes?.[event.eventTypeId];

                                    return (
                                        <div
                                            className={classNames(
                                                "flex-grow-1 list-group-item list-group-item-action ",
                                                "d-flex justify-content-between align-items-center",
                                            )}
                                            key={event.eventTypeId}
                                        >
                                            <div className="col-3">
                                                {eventType?.label}
                                            </div>

                                            <div className="text-light flex-grow-1 ">{eventType?.description}</div>
                                            <div className="">
                                                <WorkflowPicker
                                                    buttonText="Edit"
                                                    title={eventType?.label ?? ""}
                                                    input={eventType?.input?.map(input => ({
                                                        id: input.id as string,
                                                        label: input.label as string,
                                                        description: input.description as string,
                                                        input: input.input as FormField[],
                                                    })) ?? []}
                                                    type={eventType?.workflowType ?? ActionContextType.GenericNoTicket}
                                                    workflowId={event.workflowId}
                                                    onWorkflowSaved={workflowId => {
                                                        if (workflowId && events[index] !== workflowId) {
                                                            saveEvents(index, workflowId);
                                                        }
                                                    }}
                                                    buttonClassName="px-0 py-1 me-3"
                                                />
                                                <button
                                                    type="button"
                                                    aria-label="Event"
                                                    className="btn btn-none p-0 m-0"
                                                    onClick={() => {
                                                        deleted.setDeleting(index);
                                                    }}
                                                >
                                                    <BsTrash className="text-light" />
                                                </button>
                                            </div>
                                        </div>

                                    );
                                })}

                            </div>
                            <div className="d-flex align-items-middle my-3">
                                <Filters
                                    toggleText="Add Event"
                                    categories={data?.workspaceEventTypes?.filter(row => !handledEvents.has(row.id))
                                        .map(row => ({
                                            label: row.label as string,
                                            value: row.id as string,
                                        }))}
                                    selectedCategories={new Set<string>()}
                                    className=""
                                    toggleClassName=""
                                    canSelect={false}
                                    onSelected={current => {
                                        const added = Array.from(current).filter(id => !handledEvents.has(id));
                                        setEvents(events.concat(added.map(id => ({
                                            eventTypeId: id,
                                        }))));
                                    }}
                                />

                            </div>
                        </div>
                    )}

                </div>
            )}
            <div className="d-flex align-items-start gap-2">
                <ActionFormList
                    title="Actions"
                    singular="Action"
                    type={FormType.Action}
                    setting="workflow-actions"
                    description={" Actions allow you to automate your workflow. Actions allow you to invoke a set of commands"
                        + "manually from the Agent interface. Create an action, specify some fields, and kick of a set of"
                        + "actions in a list directly from the Agent interface."}
                />

                <ActionFormList
                    title="Webhooks"
                    singular="Webhook"
                    type={FormType.InboundWebhook}
                    setting="workflow-webhooks"
                    description={"Inbound webhooks allow you to receive data from outside services and "
                        + "integrations directly into Foqal Agent."}
                />

            </div>

        </div>
    );
}
export default function ActionForm(): ReactElement {
    const { Paths } = useRouting();
    const theme = useTheme();
    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);

    if (theme.isV3) {
        return id ? <ActionFormV3 id={id} /> : <ActionsV3 />;
    }

    return id ? <ActionFormSelected id={id} /> : <ActionPage />;
}
